import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API_URL from "../Config";
import { useAuth } from "../context/AuthContext";
import AssetBackdrop from "./AssetBackdrop";
import MigArchCredit from "./MigArchCredit";
import PasswordInput from "./PasswordInput";
import "./Login.css";

const MIN_PASSWORD = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [valid, setValid] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValid(false);
      return;
    }
    axios
      .post(`${API_URL}/api/password/check`, { token })
      .then(({ data }) => {
        setValid(Boolean(data.valid));
        setEmail(data.email || "");
      })
      .catch(() => setValid(false));
  }, [token]);

  useEffect(() => {
    if (!done) return undefined;
    const timer = setTimeout(() => navigate("/login", { replace: true }), 4000);
    return () => clearTimeout(timer);
  }, [done, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (!password.trim()) {
      setError("The password cannot be only spaces.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API_URL}/api/password/reset`, { token, password });
      if (isAuthenticated) await logout();
      setDone(data.message);
    } catch (err) {
      setError(err.response?.data?.message || "Could not change the password. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <AssetBackdrop />
      <div className="login-box">
        <div className="text-center">
          <div className="icon-wrapper">
            <img src="/logo-jumig-store.png" alt="JUMIG Store" />
          </div>
          <p className="login-kicker">Archive Portal</p>
          <h1 className="title">Choose a new password</h1>
          {valid && !done && <p className="subtitle">For the account using {email}.</p>}
        </div>

        {valid === null ? (
          <p className="login-message">Checking your link...</p>
        ) : done ? (
          <>
            <p className="login-message" role="status">
              {done} Taking you to sign in...
            </p>
            <Link to="/login" className="login-button login-button-link">
              Sign in now
            </Link>
          </>
        ) : !valid ? (
          <>
            <p className="login-message is-error" role="alert">
              This link has expired or has already been used. Each reset link works only once.
            </p>
            <Link to="/forgot-password" className="login-button login-button-link">
              Send a new link
            </Link>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            {error && (
              <p className="login-message is-error" role="alert">
                {error}
              </p>
            )}
            <div className="input-group">
              <PasswordInput
                placeholder="New password"
                aria-label="New password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </div>
            <p className="login-hint">At least {MIN_PASSWORD} characters. A mix of letters, numbers and symbols is safest.</p>
            <div className="input-group">
              <PasswordInput
                placeholder="Confirm new password"
                aria-label="Confirm new password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <button type="submit" className="login-button" disabled={submitting}>
              {submitting ? "Saving..." : "Change password"}
            </button>
          </form>
        )}

        <div className="login-register">
          <Link to="/login">Back to sign in</Link>
        </div>
        <MigArchCredit className="login-credit" />
      </div>
    </div>
  );
}
